import _              from 'underscore';
import classNames     from 'classnames';
import { ClassValue } from 'classnames/types';
import React, { FC }  from 'react';

import { CORVAL_SUPPORT_EMAIL, CORVAL_SUPPORT_PHONE } from '../../../../constants/SystemEnums';
import { TextLink }                                   from '../../display/TextLink';
import { useIntercomTools }                           from "../../../../hooks/useIntercom";


interface HelpAndSupportStatementProps {

    /* Suggest to user to try again */
    suggestTryAgain?: boolean,

    /* A name for who / what to contact, used by default. When set to null / empty, show the value of email or phone number. */
    contactName?: string | null | boolean,

    /* An email to display / link to */
    contactEmail?: string,

    /* A phone number to display / link to */
    contactPhone?: string,

    /* If true, show the phone number instead of email */
    usePhone?: boolean,

    /* If true, show an Intercom link to start a message */
    useIntercomMessaging?: boolean,

    /* If useIntercomMessaging = true, show a Intercom messaging statement */
    intercomMessage?: string,

    /* If true, show the pre- / post-wording for the statement */
    withPreAmble?: boolean,
    withPostAmble?: boolean,

    /* Display a clickable link? */
    withClickableLink?: boolean,

    /* Display appropriate casing? */
    withInlineCasing?: boolean,

    /* Class names provided to the wrapper container */
    className?: ClassValue,
}

const defaultProps: HelpAndSupportStatementProps = {
    suggestTryAgain:      false,
    contactName:          'Corval',
    contactEmail:         CORVAL_SUPPORT_EMAIL,
    contactPhone:         CORVAL_SUPPORT_PHONE,
    intercomMessage:      `I need Corval's assistance...`,
    withPreAmble:         true,
    withPostAmble:        true,
    withClickableLink:    true,
    withInlineCasing:     false,
    usePhone:             false,
    useIntercomMessaging: false,
    className:            null,
}

enum CHANNEL_TYPE {
    PHONE,
    EMAIL,
    INTERCOM,
}

const HelpAndSupportStatement: FC<HelpAndSupportStatementProps> = ( props ) => {

    const intercomTools = useIntercomTools();

    const channelHash = new Map<CHANNEL_TYPE, { value: string, href: string, onClick: null | ( ( e: Event ) => void ) }>( [
        [ CHANNEL_TYPE.PHONE, {
            value:   `Call ${ props.contactPhone }`,
            href:    `tel:${ props.contactPhone }`,
            onClick: null,
        } ],
        [ CHANNEL_TYPE.EMAIL, {
            value:   `Email ${ props.contactEmail }`,
            href:    `mailto:${ props.contactEmail }`,
            onClick: null,
        } ],
        [ CHANNEL_TYPE.INTERCOM, {
            value:   `Let's start a conversation`,
            href:    `#`,
            onClick: ( e ) => {

                e.preventDefault();

                intercomTools.message( props.intercomMessage );
            },
        } ],
    ] )

    // Choose a channel type
    let channelLanguageType = ( props.usePhone && !props.useIntercomMessaging )
                              ? CHANNEL_TYPE.PHONE
                              : ( props.useIntercomMessaging && !props.usePhone )
                                ? CHANNEL_TYPE.INTERCOM
                                : CHANNEL_TYPE.EMAIL;

    // Choose a channel language set
    const channelDetails       = channelHash.get( channelLanguageType ),
          hasCustomContactName = !_.isEmpty( props.contactName ),
          customContactValue   = ( props.withPreAmble )
                                 ? `Contact ${ props.contactName }`
                                 : ( props.contactName as string );

    let language       = [],
        contactValue   = hasCustomContactName
                         ? customContactValue
                         : channelDetails.value,
        contactHref    = channelDetails.href,
        contactOnClick = channelDetails.onClick;

    /* Format with suggestion */
    if( props.suggestTryAgain ){

        language.push( 'Please try again, or ' );

        /* deal with casing */
        let lowerCaseContactValue = contactValue.split( '' );
        lowerCaseContactValue[ 0 ] = lowerCaseContactValue[ 0 ].toLowerCase();
        contactValue = lowerCaseContactValue.join( '' );

    } else if( props.withInlineCasing ){

        contactValue = contactValue[ 0 ].toLowerCase() + contactValue.substring( 1 );
    }

    /* Bold the contact value if needed */
    let strongContactValue = contactValue.split( ' ' ) as any[];
    strongContactValue = [ strongContactValue[ 0 ], ' ', <strong>{ strongContactValue[ 1 ] }</strong> ];

    /* Construct the clickable link */
    let contactLink = props.withClickableLink
                      ? <strong><TextLink href={ contactHref } onClick={ contactOnClick }>{ contactValue }</TextLink></strong>
                      : !hasCustomContactName ? strongContactValue : contactValue;

    language.push( contactLink );

    if( props.withPostAmble ){

        language.push( ' for further assistance.' );
    }

    return <span className={ classNames( props.className ) }>{ language.map( ( n, i ) => <span key={ i }>{ n }</span> ) }</span>;
};

HelpAndSupportStatement.defaultProps = defaultProps;

export { HelpAndSupportStatement }
